/**
 * The match's audio director (Phase 8): turns simulation events into engine calls through the
 * pure dispatch table, derives the continuous sounds no event describes (footsteps by distance
 * walked, jets while boosting, landings) from the state each frame, and switches the music.
 */
import type { MatchEvent, MatchState, Slot } from '@steelwake/shared/combat';
import { AUDIO_BANK } from '@steelwake/shared/data';

import { cuesForEvents, type DispatchContext } from './dispatch.js';
import type { AudioEngine } from './engine.js';

/** Metres between footsteps of a walking mech. */
const STRIDE = 2.4;
const WALK_SPEED = 0.8;
/** Downward speed at touchdown that counts as a landing. */
const LANDING_SPEED = 3;

interface SeatTrack {
  walked: number;
  grounded: boolean;
  fallSpeed: number;
}

export class AudioDirector {
  private readonly seats: SeatTrack[] = [
    { walked: 0, grounded: true, fallSpeed: 0 },
    { walked: 0, grounded: true, fallSpeed: 0 },
  ];
  private suddenDeath = false;

  constructor(
    private readonly engine: AudioEngine,
    private readonly mySlot: Slot,
  ) {}

  /** Plays the cues a batch of events asks for. */
  dispatch(events: readonly MatchEvent[], state: MatchState): void {
    const ctx: DispatchContext = {
      mySlot: this.mySlot,
      positionOf: (slot) => state.mechs[slot].body.position,
    };
    for (const request of cuesForEvents(events, ctx)) {
      if (request.stop === true && request.loopKey !== undefined) {
        this.engine.stopLoop(request.loopKey);
        continue;
      }
      this.engine.play(request.cue, {
        position: request.position,
        ...(request.loopKey !== undefined ? { loopKey: request.loopKey } : {}),
      });
      if (request.duck !== undefined) this.engine.duck(request.duck, 1.2);
    }
    for (const e of events) {
      if (e.t === 'suddenDeath' && !this.suddenDeath) {
        this.suddenDeath = true;
        this.engine.playMusic(AUDIO_BANK.music.suddenDeath);
      } else if (e.t === 'roundStart' && this.suddenDeath) {
        this.suddenDeath = false;
        this.engine.playMusic(AUDIO_BANK.music.match);
      } else if (e.t === 'matchEnd') {
        this.engine.stopMusic(3);
      }
    }
  }

  /** Continuous sounds from the state: footsteps, landings, jets. */
  frame(state: MatchState, dt: number): void {
    for (const slot of [0, 1] as const) {
      const c = state.mechs[slot];
      const track = this.seats[slot];
      if (!track) continue;
      const body = c.body;
      const feet = body.position;
      const speed = Math.hypot(body.velocity.x, body.velocity.z);
      if (!c.alive) {
        this.engine.stopLoop(`jets:${String(slot)}`);
        track.grounded = body.grounded;
        continue;
      }
      if (body.grounded && !track.grounded && track.fallSpeed > LANDING_SPEED) {
        this.engine.play('land', { position: feet });
        track.walked = 0;
      }
      if (body.grounded && speed > WALK_SPEED) {
        track.walked += speed * dt;
        if (track.walked >= STRIDE) {
          track.walked -= STRIDE;
          this.engine.play('footstep', { position: feet });
        }
      } else if (!body.grounded) {
        track.walked = 0;
      }
      if (body.boosting || body.jetting) {
        this.engine.play('jets.loop', { position: feet, loopKey: `jets:${String(slot)}` });
      }
      track.grounded = body.grounded;
      track.fallSpeed = -body.velocity.y;
    }
  }

  /** The match music bed (called once the file has loaded). */
  startMusic(): void {
    this.engine.playMusic(this.suddenDeath ? AUDIO_BANK.music.suddenDeath : AUDIO_BANK.music.match);
  }

  /** Ends every loop this director keeps alive (the match is over or torn down). */
  stopAll(): void {
    for (const slot of [0, 1]) {
      this.engine.stopLoop(`jets:${String(slot)}`);
      this.engine.stopLoop(`beam:${String(slot)}`);
      this.engine.stopLoop(`vent:${String(slot)}`);
    }
  }
}
